import { Check, AlertTriangle, MinusCircle } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { NameEnquiry } from '../types'

/**
 * AF-125 — Name Enquiry verification indicator.
 *
 * Three states per the AC:
 *   - matched   → green check
 *   - mismatch  → amber warning + score
 *   - not run   → grey "N/A"
 *
 * The mismatch case is the load-bearing one — a high-value NIP with a name
 * mismatch is a strong mule-account signal, so the score must be visible.
 */
export function NameEnquiryBadge({ value }: { value: NameEnquiry }) {
  if (!value.performed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-fg ring-1 ring-inset ring-border">
        <MinusCircle size={12} aria-hidden />
        Not performed
      </span>
    )
  }

  const matched = value.matched === true
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        matched
          ? 'bg-status-success/10 text-status-success ring-status-success/30'
          : 'bg-status-pending/10 text-status-pending ring-status-pending/30',
      )}
      aria-label={matched ? 'Name enquiry matched' : 'Name enquiry mismatch'}
    >
      {matched ? (
        <>
          <Check size={12} aria-hidden /> Match
          {value.score !== null && (
            <span className="text-[0.625rem] font-mono opacity-80">{value.score}%</span>
          )}
        </>
      ) : (
        <>
          <AlertTriangle size={12} aria-hidden /> Mismatch
          {value.score !== null && (
            <span className="text-[0.625rem] font-mono opacity-80">{value.score}%</span>
          )}
        </>
      )}
    </span>
  )
}
